import React from 'react';
import { Link } from 'react-router-dom';
import './AuthCard.scss';

interface AuthCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footerText?: string;
  // Exactly one of `to` (internal route → react-router Link) or
  // `href` (external / mailto: / tel: → plain <a>). Signup's
  // "Contact support" case wants href="mailto:support@klyx.space"
  // since /support isn't a real route.
  footerLink?: {
    text: string;
    to?: string;
    href?: string;
  };
}

/**
 * AuthCard component - Layout wrapper for authentication pages
 *
 * @param title - Main heading for the card
 * @param subtitle - Optional subtitle text
 * @param children - Form content to render inside the card
 * @param footerText - Optional text before footer link (e.g., "Don't have an account?")
 * @param footerLink - Optional footer link object with text and route
 */
export const AuthCard: React.FC<AuthCardProps> = ({
  title,
  subtitle,
  children,
  footerText,
  footerLink,
}) => {
  return (
    <div className="auth-card-container">
      <div className="auth-card">
        <div className="auth-card-header">
          <h1 className="auth-card-title">{title}</h1>
          {subtitle && <p className="auth-card-subtitle">{subtitle}</p>}
        </div>

        <div className="auth-card-body">{children}</div>

        {footerText && footerLink && (
          <div className="auth-card-footer">
            <p>
              {footerText}{' '}
              {footerLink.href ? (
                <a href={footerLink.href} className="auth-card-link">
                  {footerLink.text}
                </a>
              ) : (
                <Link to={footerLink.to ?? '#'} className="auth-card-link">
                  {footerLink.text}
                </Link>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCard;
